/**
 * InvoicingSettingsPage — Paramètres facturation + Modèles d'échéanciers (Phase 3i Lot B).
 *
 * Sections :
 * 1) Facturation générale : IBAN, préfixes, validité devis, TVA, taux par défaut.
 * 2) Modèles d'échéanciers : tableau CRUD, badge "défaut", édition en modal.
 *
 * L'ancien bloc "Cycle d'acomptes" est remplacé par la bibliothèque de modèles.
 * `company.acompte_config` reste en base pour rétrocompat / fallback si aucun template.
 */
import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import RemindersSettingsBlock from "@/components/settings/RemindersSettingsBlock";
import InternalRemindersSettingsBlock from "@/components/settings/InternalRemindersSettingsBlock";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, Trash2, Star, Pencil, Download } from "lucide-react";
import BENumericInput from "@/components/quote/BENumericInput";

function schedulePreview(schedule) {
  if (!schedule || schedule.length === 0) return "—";
  return schedule.map((s) => `${s.percent}%`).join(" + ");
}

function TemplateEditor({ open, initial, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [schedule, setSchedule] = useState([]);
  const [isDefault, setIsDefault] = useState(false);
  const [saving, setSaving] = useState(false);

  // Init une seule fois par ouverture du modal ou changement de template.
  // Deps volontairement `[open, initial]` — les setters React sont stables et n'ont pas à figurer ici.
  useEffect(() => {
    if (open) {
      setName(initial?.name || "");
      setSchedule(
        initial?.schedule?.length
          ? initial.schedule.map((s) => ({ ...s }))
          : [
              { label: "À la commande", percent: 30 },
              { label: "Solde à la réception", percent: 70 },
            ],
      );
      setIsDefault(!!initial?.is_default);
    }
  }, [open, initial]);

  const total = schedule.reduce((a, s) => a + Number(s.percent || 0), 0);
  const canSave = name.trim() && total === 100 && schedule.length >= 1 && schedule.length <= 5;

  const updateStep = (i, patch) => {
    const next = [...schedule];
    next[i] = { ...next[i], ...patch };
    setSchedule(next);
  };
  const addStep = () => {
    if (schedule.length >= 5) return;
    setSchedule([...schedule, { label: "Étape", percent: 0 }]);
  };
  const removeStep = (i) => setSchedule(schedule.filter((_, k) => k !== i));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { name: name.trim(), schedule };
      let saved;
      if (initial?.id) {
        const { data } = await api.patch(`/payment-schedules/${initial.id}`, payload);
        saved = data;
        if (isDefault && !initial.is_default) {
          const { data: defData } = await api.post(`/payment-schedules/${initial.id}/default`);
          saved = defData;
        }
      } else {
        const { data } = await api.post("/payment-schedules", { ...payload, is_default: isDefault });
        saved = data;
      }
      toast.success("Modèle enregistré");
      onSaved(saved);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="border-slate-800 bg-slate-900 text-slate-100" data-testid="template-editor-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-100">
            {initial?.id ? "Modifier le modèle" : "Nouveau modèle d'échéancier"}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Les étapes doivent totaliser 100 % (1 à 5 étapes).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-slate-300">Nom du modèle</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex : Standard 30/70, 50/50, Chantier long"
              className="border-slate-700 bg-slate-950/40 text-slate-100"
              data-testid="template-name-input"
            />
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <Label className="text-slate-300">Étapes</Label>
              <span
                className={`rounded-full px-3 py-0.5 text-xs font-mono ${
                  total === 100
                    ? "bg-emerald-500/15 text-emerald-300"
                    : "bg-red-500/15 text-red-300"
                }`}
                data-testid="template-total"
              >
                Total {total}% {total === 100 ? "✓" : "(doit être 100)"}
              </span>
            </div>
            <div className="space-y-2">
              {schedule.map((step, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`template-step-${i}`}>
                  <span className="w-16 text-sm text-slate-400">Étape {i + 1}</span>
                  <Input
                    value={step.label}
                    onChange={(e) => updateStep(i, { label: e.target.value })}
                    placeholder="À la commande"
                    className="flex-1 min-w-[160px] border-slate-700 bg-slate-950/40 text-slate-100"
                    data-testid={`template-step-label-${i}`}
                  />
                  <BENumericInput
                    value={step.percent}
                    onChange={(n) =>
                      updateStep(i, { percent: Math.max(0, Math.min(100, Math.round(n))) })
                    }
                    className="w-20 border-slate-700 bg-slate-950/40 text-slate-100"
                    data-testid={`template-step-percent-${i}`}
                  />
                  <span className="text-slate-500">%</span>
                  {schedule.length > 1 && (
                    <button
                      onClick={() => removeStep(i)}
                      className="rounded p-1.5 text-red-400 hover:bg-red-500/10"
                      aria-label="Supprimer l'étape"
                      data-testid={`template-step-remove-${i}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={addStep}
              disabled={schedule.length >= 5}
              className="mt-3 border-slate-700 bg-slate-800/60 text-slate-200 hover:bg-slate-800"
              data-testid="template-add-step"
            >
              <Plus className="mr-1.5 h-3.5 w-3.5" /> Ajouter une étape
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <input
              id="is-default-chk"
              type="checkbox"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
              className="h-4 w-4 rounded border-slate-600 bg-slate-950 accent-[#C4553B]"
              data-testid="template-is-default"
            />
            <Label htmlFor="is-default-chk" className="text-sm text-slate-300 cursor-pointer">
              Définir comme modèle par défaut pour les nouveaux devis
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-slate-700 bg-slate-800/60 text-slate-200">
            Annuler
          </Button>
          <Button
            onClick={save}
            disabled={!canSave || saving}
            className="bg-[#C4553B] text-white hover:bg-[#A8402C]"
            data-testid="template-save-btn"
          >
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function InvoicingSettingsPage() {
  const { state } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorInitial, setEditorInitial] = useState(null);

  const load = async () => {
    const [{ data: settings }, { data: tmpls }] = await Promise.all([
      api.get("/company/settings"),
      api.get("/payment-schedules", { params: { include_inactive: true } }).catch(() => ({ data: [] })),
    ]);
    setForm({
      iban: settings.iban || "",
      invoice_prefix: settings.invoice_prefix || "FAC",
      quote_prefix: settings.quote_prefix || "DEV",
      default_quote_validity_days: settings.default_quote_validity_days ?? 30,
      default_vat_rate: settings.default_vat_rate ?? 21,
      // Turn 2ter — VAT retiré de ce form. Source unique : Paramètres → Entreprise.
      // Ne PAS inclure vat_number ici pour éviter d'écraser la valeur DB avec ""
      // au PATCH depuis cette page.
      invoice_due_days: settings.invoice_due_days ?? 30,
      quote_reminders_enabled: settings.quote_reminders_enabled ?? true,
      invoice_reminders_enabled: settings.invoice_reminders_enabled ?? false,
      supplier_reminders_enabled: settings.supplier_reminders_enabled ?? true,
      peppol_enabled: settings.peppol_enabled ?? false,
      peppol_endpoint_id: settings.peppol_endpoint_id || "",
    });
    setTemplates(tmpls || []);
  };
  useEffect(() => {
    load();
    // Bug #2 fix (14/09/2026) — refetch quand company change dans le même onglet.
  }, [state?.company?.id]);

  // Scroll vers le bloc "Relances" si le hash #reminders est présent (CTA Dashboard)
  useEffect(() => {
    if (!form) return;
    if (typeof window === "undefined") return;
    if (window.location.hash !== "#reminders") return;
    const t = setTimeout(() => {
      const el = document.getElementById("reminders");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
    return () => clearTimeout(t);
  }, [form]);

  if (!form) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/company/settings", form);
      toast.success("Paramètres facturation enregistrés");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const openNew = () => {
    setEditorInitial(null);
    setEditorOpen(true);
  };
  const openEdit = (tmpl) => {
    setEditorInitial(tmpl);
    setEditorOpen(true);
  };
  const setDefault = async (tmpl) => {
    try {
      await api.post(`/payment-schedules/${tmpl.id}/default`);
      toast.success(`« ${tmpl.name} » défini comme défaut`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const remove = async (tmpl) => {
    if (!window.confirm(`Supprimer le modèle « ${tmpl.name} » ?\n\nSi ce modèle est référencé par des devis existants, il sera simplement désactivé (conservé pour l'historique).`)) return;
    try {
      const res = await api.delete(`/payment-schedules/${tmpl.id}`);
      if (res.data && res.data.soft_deleted) {
        toast.success(
          `Modèle désactivé (référencé par ${res.data.quote_count} devis) — historique conservé`,
        );
      } else {
        toast.success("Modèle supprimé");
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="invoicing-settings-page">
      <div>
        <h1 className="text-2xl font-bold">Facturation & Paiements</h1>
        <p className="text-sm text-slate-400">
          IBAN, préfixes, TVA et bibliothèque de modèles d&apos;échéanciers.
        </p>
      </div>

      <Card className="border-slate-800 bg-slate-900/40">
        <CardContent className="grid grid-cols-1 gap-4 p-6 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-slate-300">
              IBAN
              {/* Lot P0 #1 (2026-09-09) — Highlight rouge si vide ou mal formé.
                  Sauvegarde reste possible avec champ vide (défaut back). */}
              {!form.iban && (
                <span
                  className="ml-2 text-xs font-normal text-[#F0C572]"
                  data-testid="iban-required-hint"
                >
                  (requis pour émission facture)
                </span>
              )}
              {form.iban && !/^BE\d{14}$/.test((form.iban || "").replace(/[.\s]/g, "")) && (
                <span
                  className="ml-2 text-xs font-normal text-red-400"
                  data-testid="iban-invalid-hint"
                >
                  (format attendu : BE + 14 chiffres)
                </span>
              )}
            </Label>
            <Input
              value={form.iban}
              onChange={(e) => setForm({ ...form, iban: e.target.value.toUpperCase() })}
              placeholder="BE68 5390 0754 7034"
              className={`bg-slate-950/40 font-mono text-slate-100 ${
                form.iban && !/^BE\d{14}$/.test((form.iban || "").replace(/[.\s]/g, ""))
                  ? "border-red-500/60"
                  : !form.iban
                  ? "border-[#F0C572]/40"
                  : "border-slate-700"
              }`}
              data-testid="iban-input"
            />
            <p className="text-xs text-slate-500">Format belge : BE + 14 chiffres (espaces optionnels).</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300">Préfixe factures</Label>
            <Input
              value={form.invoice_prefix}
              onChange={(e) => setForm({ ...form, invoice_prefix: e.target.value.toUpperCase() })}
              placeholder="FAC"
              className="border-slate-700 bg-slate-950/40 font-mono text-slate-100"
              data-testid="prefix-input"
            />
            <p className="text-xs text-slate-500">
              2–10 caractères majuscules/chiffres. Ex : {form.invoice_prefix}-2026-0001
            </p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300">Préfixe devis</Label>
            <Input
              value={form.quote_prefix}
              onChange={(e) => setForm({ ...form, quote_prefix: e.target.value.toUpperCase() })}
              placeholder="DEV"
              className="border-slate-700 bg-slate-950/40 font-mono text-slate-100"
              data-testid="quote-prefix-input"
            />
            <p className="text-xs text-slate-500">
              2–10 caractères majuscules/chiffres. Ex : {form.quote_prefix}-2026-0001
            </p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300">Validité des devis (jours)</Label>
            <BENumericInput
              value={form.default_quote_validity_days}
              onChange={(n) =>
                setForm({
                  ...form,
                  default_quote_validity_days: Math.max(1, Math.min(90, Math.round(n) || 30)),
                })
              }
              className="w-32 border-slate-700 bg-slate-950/40 text-slate-100"
              data-testid="quote-validity-input"
            />
            <p className="text-xs text-slate-500">
              1 à 90 jours. Ajouté à la date d&apos;envoi pour calculer l&apos;échéance du devis.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300">Échéance factures (jours)</Label>
            <BENumericInput
              value={form.invoice_due_days}
              onChange={(n) =>
                setForm({
                  ...form,
                  invoice_due_days: Math.max(1, Math.min(365, Math.round(n) || 30)),
                })
              }
              className="w-32 border-slate-700 bg-slate-950/40 text-slate-100"
              data-testid="invoice-due-days-input"
            />
            <p className="text-xs text-slate-500">
              1 à 365 jours. Délai avant qu&apos;une facture soit marquée « en retard ».
            </p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300">Numéro TVA</Label>
            <div className="rounded-md border border-slate-800 bg-slate-950/40 p-3 text-xs text-slate-400" data-testid="vat-moved-notice">
              Le numéro de TVA se configure désormais dans{" "}
              <a href="/settings/company" className="text-[#C4553B] underline hover:text-[#A8402C]">
                Paramètres → Entreprise
              </a>
              {" "}(bloc « Identité légale »).
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300">Taux TVA par défaut pour nouvelles lignes</Label>
            <Select
              value={String(form.default_vat_rate)}
              onValueChange={(v) => setForm({ ...form, default_vat_rate: parseInt(v, 10) })}
            >
              <SelectTrigger
                className="border-slate-700 bg-slate-950/40 text-slate-100"
                data-testid="default-vat-rate-input"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-slate-700 bg-slate-900">
                <SelectItem value="6" data-testid="default-vat-rate-option-6">
                  6 % — Rénovation habitation privée &gt;10 ans (A.R. n°20)
                </SelectItem>
                <SelectItem value="21" data-testid="default-vat-rate-option-21">
                  21 % — Standard
                </SelectItem>
                <SelectItem value="0" data-testid="default-vat-rate-option-0">
                  0 % — Cocontractant (autoliquidation B2B)
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">
              Utilisé lors de l&apos;ajout d&apos;une nouvelle ligne standard dans un devis. Les postes du catalogue conservent leur taux propre.
            </p>
          </div>
        </CardContent>
      </Card>

      <Button
        onClick={save}
        disabled={saving}
        className="bg-[#C4553B] text-white hover:bg-[#A8402C]"
        data-testid="invoicing-save-btn"
      >
        {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        Enregistrer les paramètres
      </Button>

      {/* Phase 7a — Relances IA */}
      <RemindersSettingsBlock form={form} setForm={setForm} />

      {/* Phase 10d — Rappels internes (digest factures fournisseurs) */}
      <InternalRemindersSettingsBlock form={form} setForm={setForm} />

      {/* Phase Peppol — Facturation électronique B2B BE (2026) */}
      <Card className="border-slate-800 bg-slate-900/40" data-testid="peppol-settings-card">
        <CardContent className="p-6 space-y-4">
          <div>
            <h3 className="font-semibold text-slate-200">Facturation électronique Peppol (B2B)</h3>
            <p className="mt-1 text-xs text-slate-500">
              À partir de 2026, les factures B2B belges doivent transiter par le réseau Peppol.
              Ce module envoie automatiquement chaque facture B2B (client avec n° TVA) au réseau
              via notre partenaire d&apos;accès. L&apos;émission PDF/email normale n&apos;est jamais bloquée.
            </p>
          </div>
          <label className="flex items-center gap-3 rounded border border-slate-800 bg-slate-950/30 p-3">
            <input
              type="checkbox"
              className="h-4 w-4 accent-[#C4553B]"
              checked={form.peppol_enabled}
              onChange={(e) => setForm({ ...form, peppol_enabled: e.target.checked })}
              data-testid="peppol-enabled-toggle"
            />
            <span className="text-sm text-slate-200">
              <b>Activer l&apos;envoi Peppol</b> pour toute facture B2B émise
              <div className="text-xs text-slate-500">
                Par défaut désactivé. À activer une fois vos identifiants Peppol validés côté partenaire.
              </div>
            </span>
          </label>
          <div className="space-y-1.5">
            <Label className="text-slate-300">Identifiant Peppol émetteur (endpoint sender)</Label>
            <Input
              value={form.peppol_endpoint_id}
              onChange={(e) => setForm({ ...form, peppol_endpoint_id: e.target.value.trim() })}
              placeholder="0208:BE0999888777"
              className="border-slate-700 bg-slate-950/40 font-mono text-slate-100"
              data-testid="peppol-endpoint-id-input"
            />
            <p className="text-xs text-slate-500">
              Format standard : <span className="font-mono">0208:BE0XXXXXXXXX</span>. Laisser vide
              pour dériver automatiquement de votre n° de TVA.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-800 bg-slate-900/40" data-testid="templates-card">
        <CardContent className="p-6">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="font-semibold text-slate-200">Modèles d&apos;échéanciers</h3>
              <p className="text-xs text-slate-500">
                Bibliothèque de modèles réutilisables sur vos devis. Le modèle par défaut est
                appliqué à chaque nouveau devis (surchargeable ligne par ligne).
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={async () => {
                  try {
                    const { data } = await api.post("/payment-schedules/import-sectorial");
                    if (data.created === 0) {
                      toast.info("Aucun nouveau modèle à importer (déjà présents)");
                    } else {
                      toast.success(`${data.created} modèle(s) sectoriel(s) importé(s)`, {
                        description: data.skipped > 0 ? `${data.skipped} déjà présent(s), non modifié(s)` : undefined,
                      });
                    }
                    await load();
                  } catch (e) {
                    toast.error(formatApiError(e));
                  }
                }}
                size="sm"
                variant="outline"
                className="border-slate-700 bg-slate-800/60 text-slate-200"
                data-testid="import-sectorial-btn"
              >
                <Download className="mr-1.5 h-3.5 w-3.5" /> Importer modèles sectoriels
              </Button>
              <Button
                onClick={openNew}
                size="sm"
                className="bg-[#C4553B] text-white hover:bg-[#A8402C]"
                data-testid="add-template-btn"
              >
                <Plus className="mr-1.5 h-3.5 w-3.5" /> Nouveau modèle
              </Button>
            </div>
          </div>

          {templates.length === 0 ? (
            <div className="rounded border border-dashed border-slate-700 p-6 text-center text-sm text-slate-400">
              Aucun modèle. Créez-en un pour l&apos;appliquer à vos devis.
            </div>
          ) : (
            <div className="overflow-x-auto rounded border border-slate-800">
              <table className="w-full min-w-[520px] text-sm">
                <thead className="bg-slate-800/40 text-xs uppercase text-slate-400">
                  <tr>
                    <th className="p-3 text-left">Nom</th>
                    <th className="p-3 text-left">Étapes</th>
                    <th className="p-3 text-left">Statut</th>
                    <th className="p-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {templates.map((tmpl) => (
                    <tr key={tmpl.id} data-testid={`template-row-${tmpl.id}`}>
                      <td className="p-3 font-medium text-slate-100" data-testid={`template-name-${tmpl.id}`}>
                        {tmpl.name}
                      </td>
                      <td className="p-3 text-slate-300 tabular">{schedulePreview(tmpl.schedule)}</td>
                      <td className="p-3">
                        {tmpl.is_default ? (
                          <Badge
                            className="border-[#C4553B]/40 bg-[#C4553B]/15 text-[#F0C572]"
                            data-testid={`template-default-badge-${tmpl.id}`}
                          >
                            <Star className="mr-1 h-3 w-3" /> Par défaut
                          </Badge>
                        ) : tmpl.is_active === false ? (
                          <Badge
                            className="border-slate-600 bg-slate-800/60 text-slate-400"
                            data-testid={`template-inactive-badge-${tmpl.id}`}
                            title="Ce modèle est référencé par au moins un devis existant — il est masqué du dropdown éditeur mais conservé pour l'historique."
                          >
                            Inactif
                          </Badge>
                        ) : (
                          <button
                            onClick={() => setDefault(tmpl)}
                            className="text-xs text-slate-400 underline-offset-2 hover:text-slate-200 hover:underline"
                            data-testid={`template-set-default-${tmpl.id}`}
                          >
                            Définir par défaut
                          </button>
                        )}
                      </td>
                      <td className="p-3 text-right">
                        <div className="inline-flex gap-1">
                          <button
                            onClick={() => openEdit(tmpl)}
                            className="rounded p-1.5 text-slate-300 hover:bg-slate-800"
                            aria-label="Modifier"
                            data-testid={`template-edit-${tmpl.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          {!tmpl.is_default && (
                            <button
                              onClick={() => remove(tmpl)}
                              className="rounded p-1.5 text-red-400 hover:bg-red-500/10"
                              aria-label="Supprimer"
                              data-testid={`template-delete-${tmpl.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <TemplateEditor
        open={editorOpen}
        initial={editorInitial}
        onClose={() => setEditorOpen(false)}
        onSaved={async () => {
          await load();
        }}
      />
    </div>
  );
}
